import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { Lang, LanguageService } from './language.service';

/**
 * LanguageSwitcherComponent — a small DA/EN toggle for the public SPAs. The
 * active language tracks `langChanges$`; clicking persists via LanguageService.
 * aria-labels resolve from `i18n.*` keys through the `transloco` pipe, so a live
 * switch re-renders them — proving `reRenderOnLangChange`. Story 25.5.
 */
@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [TranslocoModule, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="lang-switcher" role="group" [attr.aria-label]="'i18n.languageSwitcher.label' | transloco">
      <button
        type="button"
        class="lang-btn"
        [class.active]="(active$ | async) === 'da'"
        [attr.aria-pressed]="(active$ | async) === 'da'"
        [attr.aria-label]="'i18n.languageSwitcher.danish' | transloco"
        (click)="select('da')"
      >DA</button>
      <button
        type="button"
        class="lang-btn"
        [class.active]="(active$ | async) === 'en'"
        [attr.aria-pressed]="(active$ | async) === 'en'"
        [attr.aria-label]="'i18n.languageSwitcher.english' | transloco"
        (click)="select('en')"
      >EN</button>
    </div>
  `,
  styles: [
    `
      .lang-switcher {
        display: inline-flex;
        gap: 4px;
      }
      .lang-btn {
        border: 1px solid var(--mat-sys-outline, #c4c4c4);
        background: transparent;
        color: inherit;
        border-radius: 4px;
        padding: 2px 10px;
        font: inherit;
        line-height: 1.6;
        cursor: pointer;
      }
      .lang-btn.active {
        background: var(--mat-sys-primary, #1976d2);
        color: var(--mat-sys-on-primary, #fff);
        border-color: transparent;
      }
    `,
  ],
})
export class LanguageSwitcherComponent {
  private readonly languageService = inject(LanguageService);
  private readonly transloco = inject(TranslocoService);

  // langChanges$ is a BehaviorSubject — emits the current active lang on
  // subscribe and on every switch, so the toggle highlight stays in sync.
  protected readonly active$ = this.transloco.langChanges$;

  protected select(lang: Lang): void {
    this.languageService.set(lang);
  }
}
